import gpmfExtract from 'gpmf-extract'
import goProTelemetry from 'gopro-telemetry'
import { getDistance } from 'geolib'
import { Buffer } from 'buffer/'
import MP4Box from 'mp4box'

// Optimized GPMF extraction using larger chunks for faster processing
const fastGpmfExtract = async (file, { onProgress } = {}) => {
    return new Promise((resolve, reject) => {
        const mp4boxFile = MP4Box.createFile()
        const fileSize = file.size
        let offset = 0
        let trackId
        let nb_samples
        const timing = {}

        mp4boxFile.onError = reject

        // When MP4 structure is ready, extract the GPMF track
        mp4boxFile.onReady = (videoData) => {
            console.log('Video tracks found:', videoData.tracks.map(t => ({
                id: t.id,
                type: t.type,
                codec: t.codec,
                name: t.name,
                samples: t.nb_samples
            })))

            for (let i = 0; i < videoData.tracks.length; i++) {
                if (videoData.tracks[i].codec === 'gpmd') {
                    trackId = videoData.tracks[i].id
                    nb_samples = videoData.tracks[i].nb_samples
                    timing.start = videoData.tracks[i].created
                    timing.start.setMinutes(
                        timing.start.getMinutes() + timing.start.getTimezoneOffset()
                    )
                } else if (
                    videoData.tracks[i].type === 'video' ||
                    videoData.tracks[i].name === 'VideoHandler' ||
                    videoData.tracks[i].track_height > 0
                ) {
                    const vid = videoData.tracks[i]
                    timing.videoDuration = vid.movie_duration / vid.movie_timescale
                    timing.frameDuration = timing.videoDuration / vid.nb_samples
                }
            }

            if (trackId != null) {
                mp4boxFile.setExtractionOptions(trackId, null, {
                    nbSamples: nb_samples
                })

                mp4boxFile.onSamples = (id, user, samples) => {
                    const totalSamples = samples.reduce((acc, cur) => acc + cur.size, 0)
                    timing.samples = []
                    const uintArr = new Uint8Array(totalSamples)
                    let runningCount = 0

                    samples.forEach((sample) => {
                        timing.samples.push({ cts: sample.cts, duration: sample.duration })
                        for (let i = 0; i < sample.size; i++) {
                            uintArr.set(sample.data, runningCount)
                        }
                        runningCount += sample.size
                    })

                    resolve({ rawData: uintArr, timing })
                }

                mp4boxFile.start()
            } else {
                // Reject with proper Error object for better error handling
                reject(new Error('GPMF track not found in video. This video does not contain GPS telemetry data.'))
            }
        }

        // Use ReadableStream with adaptive chunk size based on device
        // Mobile devices (especially iPhone) have limited memory and crash with large chunks
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const CHUNK_SIZE = isMobile
            ? 1024 * 1024 * 2  // 2MB for mobile devices to prevent memory crashes
            : 1024 * 1024 * 16 // 16MB for desktop (reduced from 32MB for safety)

        const stream = file.stream()
        const reader = stream.getReader()

        const readChunk = async () => {
            try {
                const { done, value } = await reader.read()

                if (done) {
                    mp4boxFile.flush()
                    return
                }

                const buffer = value.buffer
                buffer.fileStart = offset
                mp4boxFile.appendBuffer(buffer)
                offset += value.byteLength

                // Report progress
                const progress = Math.min(offset / fileSize, 1)
                if (onProgress) {
                    onProgress(progress)
                }

                // Continue reading
                readChunk()
            } catch (error) {
                reject(error)
            }
        }

        readChunk()
    })
}

// Function to process GoPro MP4 video and extract GPS telemetry data
export const processGoProVideo = async (file, { onProgress, track } = {}) => {
    try {
        const fileSizeGB = file.size / (1024 * 1024 * 1024)
        console.log('Starting GoPro video processing...', {
            fileName: file.name,
            fileSize: file.size,
            fileSizeGB: fileSizeGB.toFixed(2) + ' GB',
            fileType: file.type
        })

        // Extract raw GPMF data from the MP4 file using optimized extractor
        console.log('Extracting GPMF data from video...')

        // Throttle progress updates to avoid excessive re-renders
        let lastProgressUpdate = 0
        const PROGRESS_THROTTLE_MS = 200 // Update at most every 200ms

        const extracted = await fastGpmfExtract(file, {
            onProgress: (progress) => {
                const now = Date.now()
                // Only update if enough time has passed or we're at 100%
                if (now - lastProgressUpdate >= PROGRESS_THROTTLE_MS || progress >= 1) {
                    lastProgressUpdate = now
                    // Cap progress at 100%
                    const percentage = Math.min(Math.round(progress * 100), 100)
                    console.log(`Extraction progress: ${percentage}%`)
                    if (onProgress) {
                        onProgress({ stage: 'extracting', progress: percentage })
                    }
                }
            }
        })

        console.log('GPMF data extracted:', {
            rawDataSize: extracted.rawData?.length || 0,
            hasRawData: !!extracted.rawData,
            rawDataType: extracted.rawData?.constructor?.name,
            timing: extracted.timing
        })

        if (!extracted.rawData || extracted.rawData.length === 0) {
            throw new Error('No GPMF data found in video file. This might not be a GoPro video with GPS enabled.')
        }

        // Convert UInt8Array to Buffer if needed (browser mode returns UInt8Array)
        let rawData = extracted.rawData
        if (rawData instanceof Uint8Array && !(rawData instanceof Buffer)) {
            console.log('Converting Uint8Array to Buffer...')
            rawData = Buffer.from(rawData)
        }

        // Make Buffer available globally for gopro-telemetry (it might need this in browser)
        if (typeof window !== 'undefined' && !window.Buffer) {
            window.Buffer = Buffer
        }

        console.log('Processing telemetry from GPMF data...', {
            dataType: rawData.constructor.name,
            dataLength: rawData.length,
            timingAvailable: !!extracted.timing,
            firstBytes: Array.from(rawData.slice(0, 20)), // Log first 20 bytes for debugging
            isBuffer: Buffer.isBuffer(rawData),
            hasValidStructure: rawData && rawData.length > 0 && typeof rawData.slice === 'function'
        })

        // Parse the GPMF data to get GPS and other telemetry
        // The gopro-telemetry library expects either a Buffer or an object with { rawData, timing }
        let telemetry
        let usedRelaxedFilter = false

        // Create the input object in the format gopro-telemetry expects
        const telemetryInput = {
            rawData: rawData,
            timing: extracted.timing
        }

        const telemetryOptions = {
            stream: ['GPS5', 'GPS9'], // Explicitly look for both GPS5 and GPS9 streams
            repeatSticky: true,
            repeatHeaders: true,
            ellipsoid: 6378137, // WGS84 ellipsoid radius
        }

        console.log('Calling goProTelemetry with input:', {
            hasRawData: !!telemetryInput.rawData,
            hasTiming: !!telemetryInput.timing,
            rawDataType: telemetryInput.rawData?.constructor?.name
        })

        if (onProgress) {
            onProgress({ stage: 'parsing', progress: 0 })
        }

        try {
            // Try with strict GPS filtering first
            telemetry = await goProTelemetry(telemetryInput, {
                ...telemetryOptions,
                GPS5Precision: 500, // Filter out samples with DOP > 500
                GPS5Fix: 2, // Require at least 2D GPS lock
            })
        } catch (strictError) {
            console.log('Strict filtering failed, trying with relaxed settings:', strictError.message)
            console.log('Trying without GPS filtering...')
            // Try again with more relaxed filtering
            try {
                telemetry = await goProTelemetry(telemetryInput, {
                    ...telemetryOptions,
                    GPS5Fix: 0, // Accept any GPS fix quality
                })
                usedRelaxedFilter = true
            } catch (relaxedError) {
                console.log('Relaxed filtering also failed, trying raw parse:', relaxedError.message)
                // Last resort: try without any GPS-specific options
                telemetry = await goProTelemetry(telemetryInput, {})
                usedRelaxedFilter = true
            }
        }

        console.log('Telemetry parsed:', {
            telemetry,
            usedRelaxedFilter,
            devices: Object.keys(telemetry)
        })

        // Find the device with GPS data (could be device '1', '2', etc.)
        let gpsStream = null

        for (const key of Object.keys(telemetry)) {
            if (telemetry[key] && telemetry[key].streams) {
                console.log(`Device ${key} streams:`, Object.keys(telemetry[key].streams))
                // Check for GPS5 or GPS9
                if (telemetry[key].streams.GPS5) {
                    gpsStream = telemetry[key].streams.GPS5
                    console.log('Found GPS5 stream in device', key)
                    break
                } else if (telemetry[key].streams.GPS9) {
                    gpsStream = telemetry[key].streams.GPS9
                    console.log('Found GPS9 stream in device', key)
                    break
                }
            }
        }

        if (!gpsStream) {
            console.error('No GPS stream found. Available telemetry:', JSON.stringify(telemetry, null, 2))
            throw new Error('No GPS data found in video. Make sure your GoPro has GPS enabled and had a GPS lock during recording. Note: HERO12, HERO 2024, HERO11 Mini, and some other models do not have GPS.')
        }

        // Extract GPS points from telemetry
        const samples = gpsStream.samples

        if (!samples || samples.length === 0) {
            throw new Error('No GPS samples found in video.')
        }

        console.log(`Found ${samples.length} GPS samples`)
        console.log('Sample structure:', samples[0])
        console.log('Sample keys:', Object.keys(samples[0]))

        // The gopro-telemetry library has already parsed and interpreted the data
        // Samples are objects with properties like: lat, lon, alt, speed, cts, date, fix, precision
        // NOT arrays with value property

        // Convert GPS samples to our point format
        // gopro-telemetry returns fully parsed data with descriptive property names
        const allPoints = samples
            .filter(sample => {
                // Filter out invalid GPS points
                // The property names include units and are descriptive
                const lat = sample['GPS (Lat.) [deg]']
                const lng = sample['GPS (Long.) [deg]']

                // Filter out points with no GPS lock (0,0 or null)
                return lat !== 0 && lng !== 0 && lat != null && lng != null &&
                       // Valid GPS coordinates range
                       lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
            })
            .map((sample, index) => {
                // Extract values using the exact property names from gopro-telemetry
                const lat = sample['GPS (Lat.) [deg]']
                const lng = sample['GPS (Long.) [deg]']
                const alt = sample['GPS (Alt.) [m]'] || 0
                const speed2d = sample['GPS (2D speed) [m/s]'] || 0
                const speed3d = sample['GPS (3D speed) [m/s]'] || 0
                const fix = sample.fix
                const dop = sample.precision

                return {
                    lat,
                    lng,
                    alt,
                    speed: speed3d || speed2d || 0, // m/s
                    time: sample.cts, // time in milliseconds
                    fix: fix, // GPS fix quality
                    dop: dop, // Dilution of precision
                    index
                }
            })

        if (allPoints.length === 0) {
            throw new Error('No valid GPS points found in video.')
        }

        console.log(`Processed ${allPoints.length} valid GPS points`)

        // Try to find the track from Typesense database if not provided
        let trackInfo = track
        if (!trackInfo && allPoints.length > 0) {
            try {
                if (onProgress) {
                    onProgress({ stage: 'finding-track', progress: 0 })
                }

                // Use the first point to search for nearby tracks
                const firstPoint = allPoints[0]

                // Dynamically import findClosestTrack to avoid circular dependencies
                const { findClosestTrack } = await import('./lineUtils.js')
                trackInfo = await findClosestTrack(firstPoint.lat, firstPoint.lng)

                if (trackInfo) {
                    console.log('Found track:', trackInfo.name)
                    console.log('Track data:', trackInfo)
                    console.log('Track location:', trackInfo.location)
                    console.log('Track combinations:', trackInfo.combinations)
                }
            } catch (error) {
                console.log('No track found or error finding track:', error.message)
                // Continue without track info
            }
        }

        if (onProgress) {
            onProgress({ stage: 'detecting-laps', progress: 0 })
        }

        // Detect laps (pass track info if available for start/finish line)
        const lapDetectionResult = detectLaps(allPoints, trackInfo)
        console.log(`Detected ${lapDetectionResult.laps.length} laps`)

        return {
            allPoints,
            allLaps: lapDetectionResult.laps,
            startFinishLat: lapDetectionResult.startFinishLat,
            startFinishLng: lapDetectionResult.startFinishLng,
            videoName: file.name,
            videoSize: file.size,
        }
    } catch (error) {
        console.error('Error processing GoPro video:', error)
        console.error('Error type:', error.constructor.name)
        console.error('Error message:', error.message)
        console.error('Error stack:', error.stack)

        // Provide more helpful error messages
        if (error.message?.includes('GPMF')) {
            throw new Error('This video does not contain GoPro metadata (GPMF). Make sure GPS was enabled when recording and the video is from a compatible GoPro camera.')
        } else if (error.message?.includes('GPS')) {
            throw error // Already has a good GPS-specific message
        } else if (error.message?.includes('buffer') || error.message?.includes('ArrayBuffer')) {
            throw new Error('Failed to read video file. The file might be corrupted or in an unsupported format.')
        } else {
            // Re-throw with original message but add context
            throw new Error(`Failed to process GoPro video: ${error.message || 'Unknown error'}`)
        }
    }
}

// Smart lap detection that uses database start/finish line or automatically finds it
// Improved with line crossing detection for closed lap circuits
const detectLaps = (points, track) => {
    if (points.length < 100) {
        return {
            laps: [{
                lapNumber: 1,
                points: points,
                lapTime: points[points.length - 1].time - points[0].time,
                formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                isClean: true
            }],
            startFinishLat: points[0].lat,
            startFinishLng: points[0].lng
        }
    }

    console.log('Starting smart lap detection...', {
        totalPoints: points.length,
        hasTrack: !!track,
        trackStartFinish: track?.combinations?.[0] ?
            { lat: track.combinations[0].startfinishlat, lng: track.combinations[0].startfinishlong } : null
    })

    let startFinishLat, startFinishLng

    // Step 1: Determine start/finish line location
    // Priority:
    // 1. Track combinations (full track data with specific start/finish)
    // 2. Track location (ONLY for type='racetrack' - for racetracks this IS the start/finish)
    // 3. Auto-detect (for go-kart tracks or if no track found)
    if (track?.combinations?.[0]?.startfinishlat && track?.combinations?.[0]?.startfinishlong) {
        // Use track's known start/finish line from combinations
        startFinishLat = track.combinations[0].startfinishlat
        startFinishLng = track.combinations[0].startfinishlong
        console.log('Using start/finish from combinations:', { lat: startFinishLat, lng: startFinishLng })
    } else if (track?.type === 'racetrack' && track?.location && Array.isArray(track.location) &&
               track.location.length >= 2 && typeof track.location[0] === 'number' &&
               typeof track.location[1] === 'number') {
        // For racetracks ONLY in Typesense, location IS the start/finish line
        // For go-kart tracks, location is just the general location, not start/finish
        startFinishLat = track.location[0]
        startFinishLng = track.location[1]
        console.log('Using racetrack location as start/finish:', { lat: startFinishLat, lng: startFinishLng })
    } else {
        // Automatically detect start/finish line by finding most frequently crossed location
        const GRID_SIZE = 0.0001 // ~11 meters
        const locationCounts = new Map()

        // Sample points to find hot spots (areas visited multiple times)
        const SAMPLE_RATE = 10
        for (let i = 0; i < points.length; i += SAMPLE_RATE) {
            const point = points[i]
            const gridKey = `${Math.floor(point.lat / GRID_SIZE)},${Math.floor(point.lng / GRID_SIZE)}`
            locationCounts.set(gridKey, (locationCounts.get(gridKey) || 0) + 1)
        }

        // Find the grid cell visited most frequently (likely the start/finish area)
        let maxCount = 0
        let bestGridKey = null
        for (const [key, count] of locationCounts) {
            if (count > maxCount) {
                maxCount = count
                bestGridKey = key
            }
        }

        if (!bestGridKey || maxCount < 3) {
            // Not enough crossings detected, use first point
            console.log('Could not find start/finish line, using entire recording as one lap')
            return {
                laps: [{
                    lapNumber: 1,
                    points: points,
                    lapTime: points[points.length - 1].time - points[0].time,
                    formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                    isClean: true
                }],
                startFinishLat: points[0].lat,
                startFinishLng: points[0].lng
            }
        }

        // Convert grid key back to lat/lng
        const [gridLat, gridLng] = bestGridKey.split(',').map(Number)
        startFinishLat = (gridLat + 0.5) * GRID_SIZE
        startFinishLng = (gridLng + 0.5) * GRID_SIZE

        console.log('Auto-detected start/finish line:', {
            lat: startFinishLat,
            lng: startFinishLng,
            crossings: maxCount
        })
    }

    // Step 2: Find all crossings using improved detection
    const CROSSING_THRESHOLD = 25 // meters - detection radius for start/finish line

    // Calculate approximate track size to adjust thresholds
    // For go-karts: typically 800-1500m per lap, ~30-60s lap times
    // For racing: typically 3000-6000m per lap, ~90-180s lap times
    let totalRecordingDistance = 0
    for (let i = 0; i < points.length - 1; i++) {
        totalRecordingDistance += getDistance(
            { latitude: points[i].lat, longitude: points[i].lng },
            { latitude: points[i + 1].lat, longitude: points[i + 1].lng }
        )
    }
    const recordingDuration = (points[points.length - 1].time - points[0].time) / 1000 // seconds
    const avgSpeed = totalRecordingDistance / recordingDuration // m/s

    // Estimate track length based on recording (assume 5-10 laps typically)
    const estimatedLaps = Math.max(5, Math.min(20, Math.round(recordingDuration / 45))) // rough estimate
    const estimatedTrackLength = totalRecordingDistance / estimatedLaps

    console.log('Track size estimation:', {
        totalDistance: Math.round(totalRecordingDistance) + 'm',
        duration: Math.round(recordingDuration) + 's',
        avgSpeed: avgSpeed.toFixed(1) + 'm/s',
        estimatedTrackLength: Math.round(estimatedTrackLength) + 'm',
        estimatedLaps
    })

    // Adaptive thresholds based on estimated track size
    const isSmallTrack = estimatedTrackLength < 2000 // Go-kart tracks
    const MIN_LAP_TIME = isSmallTrack ? 20000 : 40000 // 20s for karts, 40s for racing
    const MIN_LAP_DISTANCE = isSmallTrack ? 600 : 2000 // 600m for karts, 2000m for racing
    const AWAY_THRESHOLD = isSmallTrack ? 50 : 100 // 50m for karts, 100m for racing

    console.log('Using thresholds:', {
        trackType: isSmallTrack ? 'Small track (go-kart)' : 'Large track (racing)',
        minLapTime: (MIN_LAP_TIME / 1000) + 's',
        minLapDistance: MIN_LAP_DISTANCE + 'm',
        awayThreshold: AWAY_THRESHOLD + 'm'
    })

    const crossings = []
    let lastCrossingIndex = 0
    let lastCrossingTime = points[0].time

    // Track if we've been away from the line (ensures we detect closed laps)
    let hasBeenAway = false
    let maxDistanceSinceLastCrossing = 0

    // Check for line crossings
    let debugCounter = 0
    for (let i = 50; i < points.length; i++) {
        const point = points[i]
        const timeSinceLastCrossing = point.time - lastCrossingTime

        const distance = getDistance(
            { latitude: startFinishLat, longitude: startFinishLng },
            { latitude: point.lat, longitude: point.lng }
        )

        // Track the maximum distance we've been from the line
        if (distance > maxDistanceSinceLastCrossing) {
            maxDistanceSinceLastCrossing = distance
        }

        // Mark that we've left the start/finish area only if we get far enough away
        // This prevents detecting half laps when the track passes near the start/finish
        if (distance > AWAY_THRESHOLD) {
            hasBeenAway = true
        }

        // Skip if not enough time has passed
        if (timeSinceLastCrossing < MIN_LAP_TIME) {
            continue
        }

        const isNearLine = distance < CROSSING_THRESHOLD

        // Debug log every 1000 checks after min lap time
        if (debugCounter++ % 1000 === 0) {
            console.log(`Lap detection check at point ${i}:`, {
                distance: distance.toFixed(1) + 'm',
                isNearLine,
                hasBeenAway,
                maxDistanceAway: maxDistanceSinceLastCrossing.toFixed(1) + 'm',
                timeSinceLastCrossing: (timeSinceLastCrossing / 1000).toFixed(1) + 's'
            })
        }

        // Detect crossing: we're near the line AND we've been far away from it
        // This ensures we detect complete lap closures, not half laps
        if (isNearLine && hasBeenAway && maxDistanceSinceLastCrossing > AWAY_THRESHOLD) {
            // Check if we've traveled enough distance since last crossing
            let totalDistance = 0
            for (let j = lastCrossingIndex; j < i && j < points.length - 1; j++) {
                totalDistance += getDistance(
                    { latitude: points[j].lat, longitude: points[j].lng },
                    { latitude: points[j + 1].lat, longitude: points[j + 1].lng }
                )
            }

            if (totalDistance > MIN_LAP_DISTANCE) {
                console.log(`Lap crossing detected at point ${i}:`, {
                    distanceFromLine: distance.toFixed(1) + 'm',
                    lapTime: (timeSinceLastCrossing / 1000).toFixed(1) + 's',
                    distanceTraveled: totalDistance.toFixed(0) + 'm',
                    avgSpeed: (totalDistance / (timeSinceLastCrossing / 1000)).toFixed(1) + 'm/s',
                    maxDistanceAway: maxDistanceSinceLastCrossing.toFixed(1) + 'm'
                })

                crossings.push(i)
                lastCrossingIndex = i
                lastCrossingTime = point.time
                hasBeenAway = false
                maxDistanceSinceLastCrossing = 0

                // Skip ahead to avoid detecting same crossing multiple times
                i += 50
            }
        }
    }

    console.log(`Found ${crossings.length} lap crossings`)

    // Step 3: Create laps from crossings
    const laps = []
    let lapStart = 0

    for (let i = 0; i < crossings.length; i++) {
        const lapEnd = crossings[i]
        const lapPoints = points.slice(lapStart, lapEnd + 1)
        const lapTime = points[lapEnd].time - points[lapStart].time

        // Calculate lap distance for validation
        let lapDistance = 0
        for (let j = 0; j < lapPoints.length - 1; j++) {
            lapDistance += getDistance(
                { latitude: lapPoints[j].lat, longitude: lapPoints[j].lng },
                { latitude: lapPoints[j + 1].lat, longitude: lapPoints[j + 1].lng }
            )
        }

        laps.push({
            lapNumber: i + 1,
            points: lapPoints,
            lapTime: lapTime,
            formattedTime: formatLapTime(lapTime),
            distance: Math.round(lapDistance),
            avgSpeed: (lapDistance / (lapTime / 1000)).toFixed(1),
            isClean: true,
            startIndex: lapStart,
            endIndex: lapEnd
        })

        lapStart = lapEnd
    }

    // Add final partial lap if it meets minimum requirements
    if (lapStart < points.length - 1 && (points[points.length - 1].time - points[lapStart].time) > MIN_LAP_TIME) {
        const lapPoints = points.slice(lapStart)
        const lapTime = points[points.length - 1].time - points[lapStart].time

        laps.push({
            lapNumber: laps.length + 1,
            points: lapPoints,
            lapTime: lapTime,
            formattedTime: formatLapTime(lapTime),
            isClean: true,
            isPartial: true,
            startIndex: lapStart,
            endIndex: points.length - 1
        })
    }

    if (laps.length === 0) {
        console.log('No valid laps detected - treating entire recording as one lap')
        return {
            laps: [{
                lapNumber: 1,
                points: points,
                lapTime: points[points.length - 1].time - points[0].time,
                formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                isClean: true
            }],
            startFinishLat,
            startFinishLng
        }
    }

    console.log(`Successfully detected ${laps.length} laps:`, laps.map(l =>
        `Lap ${l.lapNumber}: ${l.formattedTime}${l.distance ? ` (${l.distance}m)` : ''}${l.isPartial ? ' [partial]' : ''}`
    ))

    return {
        laps,
        startFinishLat,
        startFinishLng
    }
}

// Format lap time from milliseconds to MM:SS.mmm
export const formatLapTime = (milliseconds) => {
    if (!milliseconds || milliseconds < 0) return '0:00.000'

    const minutes = Math.floor(milliseconds / 60000)
    const seconds = Math.floor((milliseconds % 60000) / 1000)
    const ms = Math.floor(milliseconds % 1000)

    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

// Validate GoPro telemetry data
export const validateGoProData = (points) => {
    if (!Array.isArray(points)) {
        throw new Error('Invalid data format: expected array of points')
    }

    if (points.length === 0) {
        throw new Error('No valid points found in telemetry data')
    }

    points.forEach((point, index) => {
        if (!point.lat || !point.lng) {
            throw new Error(`Invalid point at index ${index}: missing lat/lng`)
        }

        if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
            throw new Error(`Invalid point at index ${index}: lat/lng must be numbers`)
        }
    })

    return true
}
